import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { INSIGHTS } from "@/constants/insights";
import useSeo from "@/hooks/useSeo";

export default function InsightsIndex() {
  useSeo({
    title: "Insights — AI, Web & Growth Playbooks | Yugiq",
    description:
      "Practical, no-hype playbooks on AI agents, automation, website & app development and SEO for modern businesses in India.",
    canonical: "https://yugiq.in/insights",
    keywords: "AI insights, business automation blog, web development tips India, AI agents playbook",
  });

  return (
    <main className="min-h-screen bg-[var(--yg-bg)] text-white" data-testid="insights-page">
      {/* Top bar */}
      <div className="border-b border-[var(--yg-border)] backdrop-blur-xl bg-[rgba(7,7,13,0.72)] sticky top-0 z-40">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="insights-logo">
            <span className="w-9 h-9 rounded-[10px] overflow-hidden flex items-center justify-center">
              <img src="/logo.png" alt="Yugiq logo" className="w-full h-full object-cover" />
            </span>
            <span className="text-[15px] font-extrabold tracking-[-0.02em] text-white">Yugiq</span>
          </Link>
          <Link to="/" className="yg-btn yg-btn-ghost" data-testid="insights-back-home">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
      </div>

      <div className="yg-grid-bg">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-10">
          <span className="yg-eyebrow">// Insights</span>
          <h1 className="yg-display text-[clamp(38px,6vw,68px)] mt-3 text-white">
            AI, web &amp; <span className="text-[var(--yg-violet-light)]">growth.</span>
          </h1>
          <p className="mt-5 max-w-[560px] text-[15.5px] leading-relaxed text-[var(--yg-text-mute)]">
            Field notes from an AI technology company — building and automating modern businesses.
          </p>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {INSIGHTS.map((post, i) => (
            <Link
              key={post.slug}
              to={`/insights/${post.slug}`}
              className="yg-card yg-card-hover p-6 flex flex-col min-h-[236px] group"
              data-testid={`insights-index-card-${post.slug}`}
              style={{ animation: `yg-rise 600ms ${(i % 3) * 80}ms both cubic-bezier(.2,.9,.3,1)` }}
            >
              <div className="flex items-center justify-between">
                <span className="yg-mono text-[10px] tracking-[0.2em] uppercase text-[var(--yg-violet-light)]">{post.category}</span>
                <ArrowUpRight className="w-4 h-4 text-[var(--yg-text-dim)] group-hover:text-[var(--yg-violet-light)] transition-colors" />
              </div>
              <h2 className="mt-4 text-white font-extrabold text-[18px] leading-[1.2] tracking-[-0.015em]">{post.title}</h2>
              <p className="mt-3 text-[13px] leading-relaxed text-[var(--yg-text-mute)] flex-1">{post.excerpt}</p>
              <div className="mt-5 pt-4 border-t border-[var(--yg-border)] yg-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--yg-text-dim)]">
                {post.readTime}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
